//@flow

import React from "react"
import { compose } from "react-apollo"
import { FormattedMessage, injectIntl } from "react-intl"
import { TweenMax, Cubic } from "gsap"
import { isEmail } from "validator"
import SVGLoader from "../../UI/SVGLoader"
import BackgroundContainer from "../../UI/BackgroundContainer"
import TitleIcon from "../Shared/TitleIcon"
import {
  mutateSubscribeToMailingList,
  mutateSendTextNotification,
  withCurrentKioskData
} from "../queries"
import { Intro, IntroDescription, IntroTitle, ContactPanels } from "./UI"
import ContactPanel from "./ContactPanel"
import background from "./background.jpg"
import iconTalk from "../Icons/iconTalk.svg"
import iconPhone from "../Icons/iconPhone.svg"
import iconMail from "../Icons/iconMail.svg"

import type { IntlShape } from "react-intl"
import type { CurrentKiosk } from "../queries/types"
import type { RouterHistory } from "react-router-dom"

const normalizePhone = value => {
  if (!value) {
    return value
  }
  const onlyNums = value.replace(/[^\d]/g, "")
  if (onlyNums.length <= 3) {
    return onlyNums
  }
  if (onlyNums.length <= 7) {
    return `${onlyNums.slice(0, 3)}-${onlyNums.slice(3)}`
  }
  return `${onlyNums.slice(0, 3)}-${onlyNums.slice(3, 6)}-${onlyNums.slice(
    6,
    10
  )}`
}

const validatePhone = values => {
  const errors = {}
  if (!values.contact) {
    errors.contact = "kiosk.more.phone.errors.required"
  } else {
    const onlyNums = values.contact.replace(/[^\d]/g, "")
    if (onlyNums.length < 10) {
      errors.contact = "kiosk.more.phone.errors.invalid"
    }
  }
  return errors
}

const validateEmail = values => {
  const errors = {}
  if (!values.contact) {
    errors.contact = "kiosk.more.email.errors.required"
  }
  if (values.contact && !isEmail(values.contact)) {
    errors.contact = "kiosk.more.email.errors.invalid"
  }
  return errors
}

const PhonePanel = ContactPanel("contact.phone", validatePhone)
const EmailPanel = ContactPanel("contact.email", validateEmail)

type Props = {
  data: CurrentKiosk,
  history: RouterHistory,
  subscribeToMailingList: Function,
  sendTextNotification: Function,
  intl: IntlShape
}

class TellMeMoreView extends React.Component<Props> {
  introText: Array<HTMLElement>
  emailPanel: ?HTMLElement
  phonePanel: ?HTMLElement

  constructor(props: Props) {
    super(props)
    this.introText = []
    this.phonePanel = null
    this.emailPanel = null
  }

  componentDidMount() {
    TweenMax.set(this.introText, { y: "-=40", opacity: 0 })
    TweenMax.set(this.emailPanel, { x: "-=40", opacity: 0 })
    TweenMax.set(this.phonePanel, { x: "+=40", opacity: 0 })

    TweenMax.staggerTo(
      this.introText,
      0.5,
      { y: "0", opacity: 1, ease: Cubic.easeOut },
      0.2
    )
    TweenMax.to(this.emailPanel, 0.5, {
      x: "0",
      opacity: 1,
      ease: Cubic.easeOut,
      delay: 0.25
    })
    TweenMax.to(this.phonePanel, 0.5, {
      x: "0",
      opacity: 1,
      ease: Cubic.easeOut,
      delay: 0.35
    })
  }

  handleConfirmation = (confirmationType: string) => {
    const { history } = this.props
    const path = {
      pathname: `/kiosk/confirm/contact/${confirmationType}`,
      state: {
        modal: true
      }
    }
    history.push(path)
  }

  render() {
    const { subscribeToMailingList, sendTextNotification } = this.props
    const { intl: { formatMessage }, data } = this.props
    const usesVirtualKeyboard = data.currentKiosk
      ? data.currentKiosk.usesVirtualKeyboard
      : false

    return (
      <BackgroundContainer imgSrc={background} flex={true}>
        <Intro>
          <TitleIcon innerRef={el => this.introText.push(el)}>
            <SVGLoader path={iconTalk} height="100%" width="100%" fill="#fff" />
          </TitleIcon>
          <div>
            <IntroTitle innerRef={el => this.introText.push(el)}>
              <FormattedMessage id="kiosk.more.title" />
            </IntroTitle>
            <IntroDescription innerRef={el => this.introText.push(el)}>
              <FormattedMessage id="kiosk.more.intro" />
            </IntroDescription>
          </div>
        </Intro>
        <ContactPanels>
          <EmailPanel
            innerRef={el => (this.emailPanel = el)}
            icon={iconMail}
            placeholder={formatMessage({ id: "kiosk.more.email.placeholder" })}
            buttonText={formatMessage({ id: "kiosk.more.email.button" })}
            usesVirtualKeyboard={usesVirtualKeyboard}
            onSubmit={values => {
              subscribeToMailingList({ email: values.contact })
              this.handleConfirmation("email")
            }}
          />
          <PhonePanel
            innerRef={el => (this.phonePanel = el)}
            icon={iconPhone}
            disclaimer={formatMessage({ id: "kiosk.more.phone.disclaimer" })}
            placeholder={formatMessage({ id: "kiosk.more.phone.placeholder" })}
            buttonText={formatMessage({ id: "kiosk.more.phone.button" })}
            normalize={normalizePhone}
            usesVirtualKeyboard={usesVirtualKeyboard}
            onSubmit={values => {
              sendTextNotification({ phoneNumber: values.contact })
              this.handleConfirmation("phone")
            }}
          />
        </ContactPanels>
      </BackgroundContainer>
    )
  }
}

const withData = compose(
  mutateSubscribeToMailingList,
  mutateSendTextNotification,
  withCurrentKioskData
)

export default withData(injectIntl(TellMeMoreView))



// WEBPACK FOOTER //
// ./src/Kiosk/TellMeMoreView/index.js